import React from 'react'
import * as stores from '../stores/appStore'
import * as types from '../types'

interface EditPersonModalProps {
  isOpen: boolean
  onClose: () => void
  person: types.Person
}

const EditPersonModal: React.FC<EditPersonModalProps> = ({ isOpen, onClose, person }) => {
  const { updatePerson } = stores.useAppStore()
  const [formData, setFormData] = React.useState({
    firstName: person.firstName,
    lastName: person.lastName,
    email: person.email || '',
    color: person.color
  })
  const [calendars, setCalendars] = React.useState(person.calendars)
  const [isLoading, setIsLoading] = React.useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await updatePerson(person.id, {
        ...formData,
        calendars,
        updatedAt: new Date()
      })
      onClose()
    } catch (error) {
      console.error('Failed to update person:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const addCalendar = () => {
    const newCalendar: types.Calendar = {
      id: crypto.randomUUID(),
      personId: person.id,
      url: '',
      provider: 'calendly',
      name: '',
      enabled: true
    }
    setCalendars([...calendars, newCalendar])
  }

  const removeCalendar = (id: string) => {
    setCalendars(calendars.filter(c => c.id !== id))
  }

  const updateCalendar = (id: string, field: keyof types.Calendar, value: any) => {
    setCalendars(calendars.map(c =>
      c.id === id ? { ...c, [field]: value } : c
    ))
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Edit Person</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Calendar Color
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="h-10 w-20"
              />
              <span className="text-sm text-gray-500">{formData.color}</span>
            </div>
          </div>

          {/* Calendars Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Calendar Links
              </label>
              <button
                type="button"
                onClick={addCalendar}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                + Add Calendar
              </button>
            </div>
            <div className="space-y-2">
              {calendars.map((calendar) => (
                <div key={calendar.id} className="flex items-start space-x-2">
                  <div className="flex-1">
                    <input
                      type="url"
                      value={calendar.url}
                      onChange={(e) => updateCalendar(calendar.id, 'url', e.target.value)}
                      placeholder="https://calendly.com/username/event"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      required
                    />
                    {calendar.name && (
                      <div className="mt-1 text-xs text-gray-500">{calendar.name}</div>
                    )}
                  </div>
                  <label className="flex items-center space-x-1">
                    <input
                      type="checkbox"
                      checked={calendar.enabled}
                      onChange={(e) => updateCalendar(calendar.id, 'enabled', e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-xs text-gray-600">Active</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => removeCalendar(calendar.id)}
                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditPersonModal